// ReviewQueueView.jsx - Display submissions awaiting head marker review
import React, { useState } from 'react';
import { AlertTriangle, Clock, FileText } from 'lucide-react';

function ReviewQueueView({
  awaitingReview,
  homeworkNames,
  maxMarks,
  sectionStructures,
  onEditReview,
  onSendWhatsApp,
  onCopyWhatsApp,
  copiedSubmissionId,
  onReject
}) {
  const [filterValue, setFilterValue] = useState('all');
  const uniqueTypes = [...new Set(awaitingReview.map(s => s.homeworkType))];
  const filtered = filterValue === 'all' ? awaitingReview : awaitingReview.filter(s => s.homeworkType === filterValue);

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    }}>
      <h2 style={{ margin: '0 0 16px 0' }}>Submissions Awaiting Review</h2>

      {uniqueTypes.length > 1 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <button
            onClick={() => setFilterValue('all')}
            style={{
              padding: '8px 16px',
              background: filterValue === 'all' ? '#c33329' : '#f8f9fa',
              color: filterValue === 'all' ? 'white' : '#2c3e50',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px'
            }}
          >
            All ({awaitingReview.length})
          </button>
          {uniqueTypes.map(type => (
            <button
              key={type}
              onClick={() => setFilterValue(type)}
              style={{
                padding: '8px 16px',
                background: filterValue === type ? '#c33329' : '#f8f9fa',
                color: filterValue === type ? 'white' : '#2c3e50',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '13px'
              }}
            >
              {homeworkNames[type] || type} ({awaitingReview.filter(s => s.homeworkType === type).length})
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px',
          color: '#7f8c8d'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✔</div>
          <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
            No submissions awaiting review
          </div>
          <div>All caught up!</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {filtered.map(submission => (
            <div
              key={submission.id}
              style={{
                border: submission.isLate ? '2px solid #e74c3c' : '2px solid #f39c12',
                borderRadius: '8px',
                padding: '20px',
                background: submission.isLate ? '#fff5f5' : '#fffaf0'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#2c3e50',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    {submission.studentName}
                    {submission.cohortName && (
                      <span style={{ 
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#667eea',
                        background: '#eef2ff',
                        padding: '4px 12px',
                        borderRadius: '6px'
                      }}>
                        {submission.cohortName}
                      </span>
                    )}
                    {submission.isLate && (
                      <span style={{
                        background: '#e74c3c',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <AlertTriangle size={14} />
                        LATE SUBMISSION
                      </span>
                    )}
                    {submission.examExtraTimeApplied && (
                      <span style={{
                        background: '#eef2ff',
                        color: '#4338ca',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Clock size={14} />
                        EXTRA TIME {submission.examExtraTimeApplied.type === 'percentage'
                          ? `+${submission.examExtraTimeApplied.value}%`
                          : `+${submission.examExtraTimeApplied.value}min`}
                      </span>
                    )}
                    {submission.recoveredFromAbandonedSession && (
                      <span style={{
                        background: '#fef2f2',
                        color: '#b91c1c',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <AlertTriangle size={14} />
                        AUTO-RECOVERED
                      </span>
                    )}
                    {submission.submittedVia === 'exam_pdf_fallback' && (
                      <span style={{
                        background: '#f1f5f9',
                        color: '#475569',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <FileText size={14} />
                        PDF UPLOAD (NO EXAM SIM)
                      </span>
                    )}
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    fontSize: '14px',
                    color: '#7f8c8d',
                    marginBottom: '12px'
                  }}>
                    <span>{homeworkNames[submission.homeworkType] || submission.homeworkType}</span>
                    <span>Marked {submission.markedAt?.toDate().toLocaleDateString()}</span>
                    {submission.markerName && (
                      <span>| Marked by: {submission.markerName}</span>
                    )}
                    {submission.score && (
                      <span style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: '#1b998b'
                      }}>
                        {submission.score.total?.toFixed(1) || '0.0'}/{maxMarks[submission.homeworkType] || 20}
                      </span>
                    )}
                  </div>
                  
                  {submission.isLate && (
                    <div style={{ 
                      fontSize: '13px', 
                      color: '#e74c3c',
                      fontWeight: '600',
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <AlertTriangle size={14} />
                      Submitted {Math.floor(submission.minutesLate / 60)}h {submission.minutesLate % 60}m after deadline
                    </div>
                  )}
                  
                  {submission.score && (
                    <div style={{
                      padding: '12px',
                      background: 'white',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}>
                      <strong>Score Breakdown:</strong> Tech: {submission.score.technical?.toFixed(1)} | 
                      Prof: {submission.score.professional?.toFixed(1)} | 
                      {(sectionStructures?.[submission.homeworkType]?.sections || [
                        { id: 'A', name: 'Sec A' },
                        { id: 'B', name: 'Sec B' },
                        { id: 'C', name: 'Sec C' }
                      ]).map((section) => (
                        <span key={section.id}> {section.name}: {submission.score[`section${section.id}`]?.toFixed(1) ?? '0.0'} |</span>
                      ))}
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => onEditReview(submission)}
                    style={{
                      padding: '16px 32px',
                      background: '#c33329',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '16px'
                    }}
                  >
                    Edit & Review
                  </button>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <button
                      onClick={() => onSendWhatsApp(submission)}
                      disabled={!submission.studentPhone}
                      style={{
                        padding: '16px 32px',
                        background: submission.studentPhone ? '#25D366' : '#95a5a6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: submission.studentPhone ? 'pointer' : 'not-allowed',
                        fontWeight: '600',
                        fontSize: '16px',
                        opacity: submission.studentPhone ? 1 : 0.5
                      }}
                      title={submission.studentPhone ? 'Open WhatsApp chat' : 'No phone number'}
                    >
                      Send to WhatsApp
                    </button>
                    <button
                      onClick={() => onCopyWhatsApp(submission)}
                      disabled={!submission.studentPhone}
                      style={{
                        padding: '8px 32px',
                        background: copiedSubmissionId === submission.id ? '#27ae60' : '#ecf0f1',
                        color: copiedSubmissionId === submission.id ? 'white' : '#2c3e50',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: submission.studentPhone ? 'pointer' : 'not-allowed',
                        fontWeight: '600',
                        fontSize: '13px',
                        opacity: submission.studentPhone ? 1 : 0.5
                      }}
                      title="Copy message to clipboard"
                    >
                      {copiedSubmissionId === submission.id ? 'Copied!' : 'Copy message'}
                    </button>
                  </div>
                  
                  <button
                    onClick={() => onReject(submission.id)}
                    style={{
                      padding: '16px 32px',
                      background: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '16px'
                    }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ReviewQueueView;